/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-array-index-key */
import React, {
    FC, useCallback, useMemo, useState,
} from 'react';
import { useFormatting } from 'locale';
import {
    Button, Icon, Loader, Title,
    Checkbox,
    Modal,
    Infobox,
} from 'ui-library';
import Divider from 'ui-library/components/Divider';
import { ICON_ARROW_RIGHT_TRENDY, ICON_INFO_OUTLINE, ICON_TYPE_CLOSE } from 'ui-library/components/Icon';
import { useTranslation } from 'react-i18next';
import { THREEBTYPES } from 'constants/constants';
import DocBox from 'components/DocBox';
import { useCaseDetails } from 'datasource/CaseManagement/CaseDetailsManager/CaseDetailsManager';
import { ApplicationStatus, CaseOrderStatus, DocStatus } from 'components/DocBox/DocBox.types';
import { useNavigate } from 'react-router-dom';
import { CaseDocumentType } from './SingleCaseComp.types';

const SingleCaseComp: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        getAmountWithDecimals,
    } = useFormatting();

    const [caseSubmitInProgress, setCaseSubmitInProgress] = useState(false);
    const [isDocReUploadInProgress, setIsDocReUploadInProgress] = useState(false);
    const [isSignatureVerifiedInProgress, setIsSignatureVerifiedInProgress] = useState(false);
    const [modalOpen, setModalOpen] = useState('');
    const {
        id,
        status,
        type,
        typeKey,
        productName,
        productNameTranslated,
        productExternalId,
        advisor,
        createdBy,
        submitted,
        submittedBy,
        depotNumber,
        investedAmount,
        amountToBeSold,
        remainingAmount,
        documents,
        rubrik,
        partialSale,
        issueCommission,
        closeAccount,
        submitCase,
        isCaseReadyForSubmission,
        isAllDocReadyToSubmit,
        onManualSign,
        onESign,
        onDocGeneration,
        setRetryState,
        acceptDocumentSend,
        containerId,
        dfsClientId,
        isLoading,
        handleDocReUpload,
        isSignatureVerified,
        handleVerifySignature,
    } = useCaseDetails();

    const submitButtonDisabledStatus = useMemo(() => documents?.find((doc: CaseDocumentType) => doc.status !== DocStatus.ReadyToSubmit) || !isCaseReadyForSubmission || caseSubmitInProgress, [documents, isCaseReadyForSubmission, caseSubmitInProgress]);
    const allDocumentsGenerated = useMemo(() => !documents?.find((doc: CaseDocumentType) => doc.status === DocStatus.ReadyToGenerate), [documents]);
    const showSubmitButton = useMemo(() => (
        allDocumentsGenerated
        && status !== CaseOrderStatus.Cancelled
        && status !== CaseOrderStatus.Submitted
    ), [allDocumentsGenerated, status]);

    const handleSubmitClick = useCallback(async () => {
        if (showSubmitButton && showSubmitButton) {
            setCaseSubmitInProgress(true);
            try {
                await submitCase();

                if (containerId) {
                    setModalOpen('with-container');
                } else {
                    setModalOpen('without-container');
                }
            } finally {
                setCaseSubmitInProgress(false);
            }
        }
    }, [submitCase, id, typeKey, containerId, showSubmitButton, showSubmitButton]);

    const onDocReUpload = useCallback(async (documentId) => {
        setIsDocReUploadInProgress(true);
        try {
            await handleDocReUpload(documentId);
        } finally {
            setIsDocReUploadInProgress(false);
        }
    }, [handleDocReUpload]);

    const onSignatureVerify = useCallback(async () => {
        setIsSignatureVerifiedInProgress(true);
        try {
            await handleVerifySignature();
        } finally {
            setIsSignatureVerifiedInProgress(false);
        }
    }, [handleVerifySignature]);

    const handleGoToApplicationDetails = useCallback(() => {
        if (containerId && dfsClientId) {
            navigate(`/client/${dfsClientId}/container/${containerId}`);
        }
    }, [containerId, dfsClientId]);

    const isPK = productExternalId === THREEBTYPES.pk;

    const enableSignatureVerifyCheckbox = useMemo(() => [CaseOrderStatus.ReadyToSubmit, CaseOrderStatus.Submitted]?.includes(status as CaseOrderStatus) || isAllDocReadyToSubmit, [status, isAllDocReadyToSubmit]);

    return (
        <>
            {status === CaseOrderStatus.Cancelled && (
                <Infobox info icon={ICON_INFO_OUTLINE} className="mb-32">
                    {t('CaseDetails.SingleCase.Info.CancelledCase')}
                </Infobox>
            )}
            <div className="case-details__content">
                <div
                    className={`single-case-section${status === CaseOrderStatus.Cancelled ? ' single-case-section-cancelled' : ''}`}
                >
                    {isLoading ? <Loader /> : (
                        <>

                            <Title type="3">
                                <strong>{t(type)}</strong>
                                {' '}
                                {!isPK ? `${productNameTranslated} -` : ''}
                                {' '}
                                {productName}
                                {rubrik ? ` <${rubrik}>` : ' '}
                            </Title>

                            <div className="case--row">
                                <div className="case-info-grid">
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.Advisor')}
                                        </Title>
                                        {advisor}
                                    </div>
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.CreatedBy')}
                                        </Title>
                                        {createdBy}
                                    </div>
                                    <Divider direction="vertical" />

                                    {/* Columns only shown if case type is CHANGE || SELL || BUY */}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.DepotNumber')}
                                        </Title>
                                        {depotNumber || '-'}
                                    </div>

                                    {/* Columns only shown if case type is SELL || BUY */}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.InvestedAmount')}
                                        </Title>
                                        {getAmountWithDecimals(investedAmount || 0)}
                                    </div>

                                    {/* Columns only shown if case type is SELL */}
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.ToBeSold')}
                                        </Title>
                                        {getAmountWithDecimals(amountToBeSold || 0)}
                                    </div>

                                    {partialSale && (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t('createNewCase.TypeOfOrder.remainingAmount')}
                                            </Title>
                                            {getAmountWithDecimals(remainingAmount || 0)}
                                        </div>
                                    )}

                                    {/* Columns only shown if case type is SELL */}

                                    {isPK && !partialSale && (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t('CaseDetails.SingleCase.CloseAccount')}
                                            </Title>
                                            {closeAccount ? t('3b.kyc.radioYes') : t('3b.kyc.radioNo')}
                                        </div>
                                    )}

                                    {isPK && (
                                        <div className="case-info-grid-col">
                                            <Title type="5">
                                                {t('CaseDetails.SingleCase.IssueCommission')}
                                            </Title>
                                            {issueCommission ? t('3b.kyc.radioYes') : t('3b.kyc.radioNo')}
                                        </div>
                                    )}

                                    <Divider direction="vertical" />

                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.Submitted')}
                                        </Title>
                                        {submitted || '-'}
                                    </div>
                                    <div className="case-info-grid-col">
                                        <Title type="5">
                                            {t('ApplicationDetails.AdvisorySection.ProductApplications.SubmittedBy')}
                                        </Title>
                                        {submittedBy || '-'}
                                    </div>
                                </div>

                                <div className="case-footer-grid">
                                    <div className="single-case--grid-col">
                                        {documents?.map(
                                            (document, index) => (
                                                <DocBox
                                                    isSentCase={false}
                                                    key={`${document?.documentName?.replaceAll(' ', '_')}_${index}`}
                                                    type={document.type}
                                                    status={status as ApplicationStatus}
                                                    uploadStatus={document?.status}
                                                    documentName={document?.documentName}
                                                    documentId={document?.documentId}
                                                    allowSigning
                                                    canESign
                                                    onManualSign={onManualSign}
                                                    onESign={onESign}
                                                    onDocGeneration={onDocGeneration}
                                                    setRetryState={setRetryState}
                                                    acceptDocumentSend={acceptDocumentSend}
                                                    isCaseDetails
                                                    handleDocReUpload={onDocReUpload}
                                                    isDocReUploadInProgress={isDocReUploadInProgress}
                                                />
                                            ),
                                        )}
                                    </div>
                                </div>

                                <div className="case-footer-grid mt-30 flex-nowrap">
                                    <div className={`signature-verified-row ${!enableSignatureVerifyCheckbox ? 'disabled' : ''}`}>
                                        <label htmlFor="signature-verified">{t('CaseDetails.SingleCase.SignatureVerification.Label')}</label>
                                        <Checkbox id="signature-verified" onChange={onSignatureVerify} checked={isSignatureVerified} disabled={isSignatureVerified || isSignatureVerifiedInProgress} label={t('CaseDetails.SingleCase.SignatureVerification.Checkbox.Label')} />
                                    </div>

                                    {showSubmitButton && (
                                        <div className="align-end">
                                            <div className="single-case--action">
                                                <Button
                                                    type="primary"
                                                    disabled={submitButtonDisabledStatus || !isSignatureVerified}
                                                    onClick={handleSubmitClick}
                                                >
                                                    {caseSubmitInProgress ? <Loader /> : (
                                                        <>
                                                            {t('CaseDetails.SingleCase.Submit')}
                                                            <Icon className="ml-8" type={ICON_ARROW_RIGHT_TRENDY} size={24} />
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {modalOpen === 'with-container' && (
                <Modal
                    title={t('CaseDetails.SingleCase.Modal.GoToApplications.title')}
                    visible
                    okText={t('CaseDetails.SingleCase.Modal.OkText')}
                    cancelText={t('clientDashboard.close')}
                    onOk={handleGoToApplicationDetails}
                    okButtonProps={{ disabled: false }}
                    cancelButtonProps={{ disabled: false }}
                    onCancel={() => setModalOpen('')}
                    width={610}
                    className="go-to-applications-modal"
                    borderRadius={24}
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16}/>}
                >
                    <Title type={3}>
                        {t('CaseDetails.SingleCase.Modal.GoToApplications.Confirmation')}
                    </Title>
                </Modal>
            )}

            {modalOpen === 'without-container' && (
                <Modal
                    visible
                    okText={t('clientDashboard.close')}
                    onOk={() => setModalOpen('')}
                    onCancel={() => setModalOpen('')}
                    okType="secondary"
                    width={610}
                    cancelText={null}
                    className="thanks-for-submitting-modal"
                    borderRadius={24}
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16}/>}
                >
                    <Title type={2}>
                        {t('CaseDetails.SingleCase.Modal.GoToApplications.title')}
                    </Title>
                </Modal>
            )}
        </>
    );
};

export default React.memo(SingleCaseComp);
